import {
  programmeChecksQuery,
  programmeQuery,
  programmeTasksQuery,
  useRemoveProgrammeCheckMutation,
} from "@common/api/maintenance-programme"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { handleError } from "@common/utils"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  useCanGoBack,
  useNavigate,
  useRouter,
} from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"
import { revisionLabel } from "@/routes/_layout/maintenance-programmes/-lib/revision-label"
import { type Phase, phasesText, type Task } from "../../-lib/items"
import { useViewedRevision } from "../../-lib/revision"
import { CHECKS, TASKS } from "../../-lib/search"

const REMOVABLE =
  "The phase stays in earlier revisions. Adding it back later creates a new phase."

export const Route = createFileRoute(
  "/_layout/maintenance-programmes_/$programmeId/phases/remove/$phaseId",
)({
  component: RemovePhaseDialog,
})

function blockerOf(
  phaseId: string,
  tasks: Task[],
  phases: Phase[],
): string | undefined {
  const count = tasks.filter((task) => task.check_ids.includes(phaseId)).length
  const including = phases.filter((phase) =>
    phase.included_check_ids.includes(phaseId),
  )
  const reasons = [taskBlocker(count), inclusionBlocker(including)].filter(
    (reason) => reason !== undefined,
  )
  return reasons.length > 0 ? reasons.join(" ") : undefined
}

function taskBlocker(count: number): string | undefined {
  if (count === 1) {
    return "It has a task. Move it to another phase first."
  }
  return count > 1
    ? `It has ${count} tasks. Move them to another phase first.`
    : undefined
}

function inclusionBlocker(including: Phase[]): string | undefined {
  if (including.length === 0) return undefined
  const codes = phasesText(including)
  const verb = including.length === 1 ? "includes" : "include"
  return `${codes} ${verb} it. Remove it from ${codes} first.`
}

function RemovePhaseDialog(): React.ReactElement {
  const { programmeId, phaseId } = Route.useParams()
  const { data: programme } = useSuspenseQuery(
    programmeQuery({ programme_id: programmeId }),
  )
  const revision = useViewedRevision(programme)
  const scope = { programme_id: programmeId, revision_id: revision?.id ?? "" }
  const enabled = revision !== undefined
  const { data: tasks } = useQuery({
    ...programmeTasksQuery({ ...scope, ...TASKS }),
    enabled,
  })
  const { data: phases } = useQuery({
    ...programmeChecksQuery({ ...scope, ...CHECKS }),
    enabled,
  })
  const mutation = useRemoveProgrammeCheckMutation()
  const navigate = useNavigate()
  const router = useRouter()
  const canGoBack = useCanGoBack()
  const showToast = useCustomToast()
  const phase = phases?.data.find(({ id }) => id === phaseId)
  const label = phase?.code ?? "this phase"
  const blocker =
    tasks && phases ? blockerOf(phaseId, tasks.data, phases.data) : undefined
  const toList = () =>
    navigate({
      to: "/maintenance-programmes/$programmeId",
      params: { programmeId },
      search: true,
    })
  const back = () => (canGoBack ? router.history.back() : toList())

  const confirm = () =>
    revision &&
    mutation.mutate(
      { ...scope, check_id: phaseId },
      {
        onSuccess: () => {
          showToast(
            `Removed ${label}`,
            revisionLabel(revision.number),
            "success",
          )
          toList()
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <ConfirmDialog
      title={`Remove ${label} from ${revision ? revisionLabel(revision.number) : "the revision"}?`}
      description={blocker ?? REMOVABLE}
      action="Remove"
      pendingAction="Removing…"
      pending={mutation.isPending}
      disabled={!phase || blocker !== undefined}
      onConfirm={confirm}
      onCancel={back}
    />
  )
}
